import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase as _supabase } from '@/integrations/supabase/client';
import type { OutboundStatus } from './useOutboundQueue';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

interface UpdateStatusPayload {
  assignmentId: string;
  sdrId: string;
  status: OutboundStatus;
  notes?: string;
  incrementAttempts?: boolean;
}

export function useOutboundActions() {
  const queryClient = useQueryClient();

  const updateStatus = useMutation({
    mutationFn: async ({
      assignmentId,
      status,
      notes,
      incrementAttempts,
    }: UpdateStatusPayload) => {
      const updatePayload: Record<string, unknown> = { status };
      if (notes !== undefined) updatePayload.notes = notes;
      if (incrementAttempts) {
        // Increment call_attempts via RPC-style update
        const { data: current } = await supabase
          .from('outbound_daily_assignments')
          .select('call_attempts')
          .eq('id', assignmentId)
          .single();
        updatePayload.call_attempts = (current?.call_attempts ?? 0) + 1;
      }

      const { error } = await supabase
        .from('outbound_daily_assignments')
        .update(updatePayload)
        .eq('id', assignmentId);

      if (error) throw error;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['outbound-queue', variables.sdrId] });
      queryClient.invalidateQueries({ queryKey: ['outbound-stats-db', variables.sdrId] });
      queryClient.invalidateQueries({ queryKey: ['outbound-batches', variables.sdrId] });
    },
  });

  /**
   * Marca a detecção do AMD como falso positivo (não era humano) e devolve
   * o contato para a fila como "pendente", sem contar tentativa.
   */
  const flagAmdFalsePositive = useMutation({
    mutationFn: async ({
      attemptId,
      assignmentId,
      sdrId,
    }: {
      attemptId: string;
      assignmentId: string | null;
      sdrId: string;
    }) => {
      const { error: attemptError } = await supabase
        .from('dialer_attempts')
        .update({
          amd_false_positive: true,
          amd_false_positive_at: new Date().toISOString(),
          amd_false_positive_by: sdrId,
          outcome: 'machine',
          amd_reason: 'amd_falso_positivo',
        })
        .eq('id', attemptId);
      if (attemptError) throw attemptError;

      if (assignmentId) {
        const { error } = await supabase
          .from('outbound_daily_assignments')
          .update({ status: 'pending', updated_at: new Date().toISOString() })
          .eq('id', assignmentId);
        if (error) throw error;
      }
      return sdrId;
    },
    onSuccess: (sdrId) => {
      queryClient.invalidateQueries({ queryKey: ['outbound-queue', sdrId] });
      queryClient.invalidateQueries({ queryKey: ['outbound-stats-db', sdrId] });
      queryClient.invalidateQueries({ queryKey: ['dialer-attempts'] });
    },
  });

  /**
   * Corrige manualmente o desfecho de uma tentativa (ex.: AMD marcou caixa postal
   * mas o cliente atendeu e desligou). Ajusta também o item da fila.
   */
  const reclassifyAttempt = useMutation({
    mutationFn: async ({
      attemptId,
      assignmentId,
      sdrId,
      outcome,
    }: {
      attemptId: string;
      assignmentId: string | null;
      sdrId: string;
      outcome: 'human' | 'machine' | 'no_answer' | 'busy' | 'failed';
    }) => {
      const { error } = await supabase
        .from('dialer_attempts')
        .update({ outcome, amd_reason: 'corrigido_pelo_sdr' })
        .eq('id', attemptId);
      if (error) throw error;

      if (assignmentId) {
        const { error: assignError } = await supabase
          .from('outbound_daily_assignments')
          .update({
            status: outcome === 'human' ? 'pending' : 'called_no_answer',
            updated_at: new Date().toISOString(),
          })
          .eq('id', assignmentId);
        if (assignError) throw assignError;
      }
      return sdrId;
    },
    onSuccess: (sdrId) => {
      queryClient.invalidateQueries({ queryKey: ['outbound-queue', sdrId] });
      queryClient.invalidateQueries({ queryKey: ['outbound-stats-db', sdrId] });
      queryClient.invalidateQueries({ queryKey: ['dialer-attempts'] });
    },
  });

  const updateNotes = useMutation({
    mutationFn: async ({
      assignmentId,
      notes,
      sdrId,
    }: {
      assignmentId: string;
      notes: string;
      sdrId: string;
    }) => {
      const { error } = await supabase
        .from('outbound_daily_assignments')
        .update({ notes })
        .eq('id', assignmentId);
      if (error) throw error;
      return sdrId;
    },
    onSuccess: (sdrId) => {
      queryClient.invalidateQueries({ queryKey: ['outbound-queue', sdrId] });
    },
  });

  return { updateStatus, updateNotes, flagAmdFalsePositive, reclassifyAttempt };
}


